/**
 * Pure Excalidraw element factories (plain JSON objects, no Excalidraw
 * import) — used by the template builders, sticky notes and the extra shapes.
 * The runtime passes their output through `restoreElements` (text metrics,
 * binding repair, fractional indices) before it enters the scene.
 *
 * Seeds and version nonces derive from the element id, so the same builder
 * call produces byte-identical elements on every client (templates rely on
 * that to make concurrent seeding converge).
 */

/**
 * Excalidraw font families. `Brand` = 6: Excalidraw's "Nunito" slot, which this app serves as Karla
 * (Eos Lightmedia's brand font — see web/shared/vite.fonts.ts).
 */
export const FONT = { Virgil: 1, Helvetica: 2, Cascadia: 3, Excalifont: 5, Nunito: 6, Brand: 6, LilitaOne: 7, ComicShanns: 8, LiberationSans: 9 } as const;
export const LINE_HEIGHT: Record<number, number> = { 1: 1.25, 2: 1.15, 3: 1.2, 5: 1.25, 6: 1.35, 7: 1.15, 8: 1.25, 9: 1.15 };

/** FNV-1a 32-bit → positive int (deterministic seed / nonce from an id). */
export function hashId(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) % 2 ** 31;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type El = Record<string, any> & { id: string; type: string; version: number; versionNonce: number; isDeleted: boolean };

export interface BaseProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  angle?: number;
  strokeColor?: string;
  backgroundColor?: string;
  fillStyle?: 'hachure' | 'cross-hatch' | 'solid' | 'zigzag';
  strokeWidth?: number;
  strokeStyle?: 'solid' | 'dashed' | 'dotted';
  roughness?: number;
  opacity?: number;
  roundness?: null | { type: number; value?: number };
  groupIds?: string[];
  frameId?: string | null;
  boundElements?: { id: string; type: 'arrow' | 'text' }[] | null;
  locked?: boolean;
  link?: string | null;
  customData?: Record<string, unknown>;
  /** Defaults to a hash of the id (deterministic). */
  versionNonce?: number;
  seed?: number;
  version?: number;
  updated?: number;
}

export function baseElement(type: string, id: string, p: BaseProps = {}): El {
  return {
    id,
    type,
    x: p.x ?? 0,
    y: p.y ?? 0,
    width: p.width ?? 0,
    height: p.height ?? 0,
    angle: p.angle ?? 0,
    strokeColor: p.strokeColor ?? '#1e1e1e',
    backgroundColor: p.backgroundColor ?? 'transparent',
    fillStyle: p.fillStyle ?? 'solid',
    strokeWidth: p.strokeWidth ?? 2,
    strokeStyle: p.strokeStyle ?? 'solid',
    roughness: p.roughness ?? 1,
    opacity: p.opacity ?? 100,
    groupIds: p.groupIds ?? [],
    frameId: p.frameId ?? null,
    index: null,
    roundness: p.roundness === undefined ? null : p.roundness,
    seed: p.seed ?? hashId(`seed:${id}`),
    version: p.version ?? 1,
    versionNonce: p.versionNonce ?? hashId(`nonce:${id}`),
    isDeleted: false,
    boundElements: p.boundElements ?? null,
    updated: p.updated ?? 1,
    link: p.link ?? null,
    locked: p.locked ?? false,
    ...(p.customData ? { customData: p.customData } : {}),
  };
}

export interface TextProps extends BaseProps {
  text: string;
  fontSize?: number;
  fontFamily?: number;
  textAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  containerId?: string | null;
}

/** Text element; width/height are estimates until `restoreElements(…, {refreshDimensions})`. */
export function textElement(id: string, p: TextProps): El {
  const fontSize = p.fontSize ?? 20;
  const fontFamily = p.fontFamily ?? FONT.Brand;
  const lineHeight = LINE_HEIGHT[fontFamily] ?? 1.25;
  const lines = p.text.split('\n');
  const estW = Math.max(...lines.map((l) => l.length)) * fontSize * 0.55;
  const estH = lines.length * fontSize * lineHeight;
  return {
    ...baseElement('text', id, { strokeWidth: 2, ...p, width: p.width ?? estW, height: p.height ?? estH, backgroundColor: 'transparent' }),
    fontSize,
    fontFamily,
    text: p.text,
    originalText: p.text,
    textAlign: p.textAlign ?? (p.containerId ? 'center' : 'left'),
    verticalAlign: p.verticalAlign ?? (p.containerId ? 'middle' : 'top'),
    containerId: p.containerId ?? null,
    autoResize: true,
    lineHeight,
  };
}

export function shapeElement(type: 'rectangle' | 'ellipse' | 'diamond', id: string, p: BaseProps): El {
  return baseElement(type, id, { roundness: type === 'rectangle' ? { type: 3 } : type === 'diamond' ? { type: 2 } : null, ...p });
}

/** Shape with a centred bound label → [shape, text]. */
export function labelledShape(
  type: 'rectangle' | 'ellipse' | 'diamond',
  id: string,
  p: BaseProps & { label: string; fontSize?: number; fontFamily?: number },
): El[] {
  const textId = `${id}-label`;
  const shape = shapeElement(type, id, { ...p, boundElements: [...(p.boundElements ?? []), { id: textId, type: 'text' }] });
  const t = textElement(textId, {
    text: p.label,
    fontSize: p.fontSize ?? 20,
    fontFamily: p.fontFamily,
    containerId: id,
    strokeColor: p.strokeColor,
    roughness: p.roughness,
    frameId: p.frameId,
    groupIds: p.groupIds,
  });
  return [shape, centreIn(t, shape)];
}

/** Position a bound text element in the middle of its container (or arrow midpoint). */
export function centreIn(text: El, container: El): El {
  let cx: number;
  let cy: number;
  if (container.type === 'arrow' || container.type === 'line') {
    const pts = container.points as [number, number][];
    const [mx, my] = polylineMidpoint(pts);
    cx = container.x + mx;
    cy = container.y + my;
  } else {
    cx = container.x + container.width / 2;
    cy = container.y + container.height / 2;
  }
  return { ...text, x: cx - text.width / 2, y: cy - text.height / 2 };
}

export function polylineMidpoint(pts: readonly (readonly [number, number])[]): [number, number] {
  if (pts.length === 0) return [0, 0];
  const segs: number[] = [];
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    const d = Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
    segs.push(d);
    total += d;
  }
  let half = total / 2;
  for (let i = 1; i < pts.length; i++) {
    const d = segs[i - 1];
    if (half <= d && d > 0) {
      const t = half / d;
      return [pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * t, pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * t];
    }
    half -= d;
  }
  return [pts[pts.length - 1][0], pts[pts.length - 1][1]];
}

export interface ArrowProps extends BaseProps {
  /** Absolute points; the element's x/y is the first point. */
  points: [number, number][];
  start?: { id: string; gap?: number; focus?: number } | null;
  end?: { id: string; gap?: number; focus?: number } | null;
  label?: string;
  startArrowhead?: string | null;
  endArrowhead?: string | null;
}

/** Arrow bound to its start/end shapes (+ optional bound label) → [arrow, label?]. */
export function arrowElement(id: string, p: ArrowProps): El[] {
  const [x0, y0] = p.points[0];
  const rel = p.points.map(([x, y]) => [x - x0, y - y0] as [number, number]);
  const xs = rel.map((q) => q[0]);
  const ys = rel.map((q) => q[1]);
  const labelId = p.label ? `${id}-label` : null;
  const arrow: El = {
    ...baseElement('arrow', id, {
      ...p,
      x: x0,
      y: y0,
      width: Math.max(...xs) - Math.min(...xs),
      height: Math.max(...ys) - Math.min(...ys),
      roundness: p.roundness === undefined ? { type: 2 } : p.roundness,
      boundElements: labelId ? [{ id: labelId, type: 'text' }] : null,
    }),
    points: rel,
    lastCommittedPoint: null,
    startBinding: p.start ? { elementId: p.start.id, focus: p.start.focus ?? 0, gap: p.start.gap ?? 8 } : null,
    endBinding: p.end ? { elementId: p.end.id, focus: p.end.focus ?? 0, gap: p.end.gap ?? 8 } : null,
    startArrowhead: p.startArrowhead ?? null,
    endArrowhead: p.endArrowhead === undefined ? 'arrow' : p.endArrowhead,
    elbowed: false,
  };
  if (!labelId) return [arrow];
  const label = textElement(labelId, { text: p.label!, fontSize: 16, containerId: id, strokeColor: p.strokeColor, frameId: p.frameId, groupIds: p.groupIds });
  return [arrow, centreIn(label, arrow)];
}

/** Closed polygon drawn as a filled `line` (Excalidraw has no triangle/star shape). */
export function polygonElement(id: string, p: BaseProps & { points: [number, number][] }): El {
  const pts = [...p.points, p.points[0]];
  const [x0, y0] = pts[0];
  const rel = pts.map(([x, y]) => [x - x0, y - y0] as [number, number]);
  const xs = rel.map((q) => q[0]);
  const ys = rel.map((q) => q[1]);
  return {
    ...baseElement('line', id, { ...p, x: x0, y: y0, width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) }),
    points: rel,
    lastCommittedPoint: null,
    startBinding: null,
    endBinding: null,
    startArrowhead: null,
    endArrowhead: null,
  };
}

export function trianglePoints(cx: number, cy: number, size: number): [number, number][] {
  const h = size * 0.866;
  return [
    [cx, cy - h / 2],
    [cx + size / 2, cy + h / 2],
    [cx - size / 2, cy + h / 2],
  ];
}

export function starPoints(cx: number, cy: number, outer: number, inner = outer * 0.45, spikes = 5): [number, number][] {
  const pts: [number, number][] = [];
  for (let i = 0; i < spikes * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = -Math.PI / 2 + (i * Math.PI) / spikes;
    pts.push([Math.round((cx + r * Math.cos(a)) * 100) / 100, Math.round((cy + r * Math.sin(a)) * 100) / 100]);
  }
  return pts;
}

export interface FrameProps extends BaseProps {
  name: string;
}
export function frameElement(id: string, p: FrameProps): El {
  return { ...baseElement('frame', id, { roughness: 0, strokeWidth: 2, backgroundColor: 'transparent', ...p }), name: p.name };
}
